/**
 * Brand page — fills the product rows, removes what is empty, and keeps the
 * merchandising order intact while doing it.
 *
 * Each row names a category collection; it is fetched through the Section
 * Rendering API with this brand as the vendor filter, so the cards are real
 * Liquid with live prices. A row the brand has nothing in is removed, and a
 * module whose rows are all gone is removed with its gateway tile — never
 * reordered, never filled with something else (see the brief).
 *
 * Also: the page tailored to the garage's active machine when it is this
 * make (2026-10-13), and the machine-family tiles derived from the fitment
 * taxonomy.
 */
(function () {
  'use strict';

  function page() {
    return document.querySelector('[data-brand-page]');
  }

  /* ---- Rows ------------------------------------------------------------- */

  /** Each row's URL: its category collection, filtered to this brand. */
  function rowUrl(row, brand) {
    return `${row.dataset.sourceUrl}?filter.p.vendor=${encodeURIComponent(brand)}&sort_by=best-selling&sections=product-row`;
  }

  /** A module with no rows left goes, and so does its gateway tile. */
  function prune(root) {
    for (const module of root.querySelectorAll('[data-brand-module]')) {
      if (!module.querySelector('[data-brand-subrow]')) module.remove();
    }
    for (const tile of root.querySelectorAll('[data-brand-tile]')) {
      if (!root.querySelector(`[data-brand-module="${tile.dataset.brandTile}"]`)) tile.remove();
    }
  }

  /**
   * Fill every row through ProductRow (two at a time, retries, a session
   * cache), remove each empty one as it lands, then prune empty modules. A
   * row is fetched once however many fills are asked for.
   */
  function fillRows(root) {
    const brand = root.dataset.brand;
    const rows = [...root.querySelectorAll('[data-brand-subrow]:not([data-brand-fetching])')];
    for (const row of rows) row.setAttribute('data-brand-fetching', '');
    if (!window.ProductRow) {
      for (const row of rows) row.remove();
      prune(root);
      return Promise.resolve();
    }
    return window.ProductRow.fill(
      rows.map((el) => ({ el, url: rowUrl(el, brand) })),
      { onRow: (el, count) => { if (!count) el.remove(); } },
    ).then(() => prune(root));
  }

  /* ---- Your machine ------------------------------------------------------- */

  /**
   * The garage's machine is this make: the page is tailored to it — the
   * line names it and offers another, the machine block beneath fills
   * (vehicle-parts.js, data-only-make) and the finder steps aside. Any
   * other machine, or none: the finder, narrowed to the make.
   */
  function paintActive(root) {
    const tailored = root.querySelector('[data-brand-tailored]');
    const finder = root.querySelector('[data-brand-finder]');
    if (!tailored) return;
    const current = window.Garage && window.Garage.active();
    const brand = (root.dataset.brand || '').toLowerCase();
    const mine = !!current && String(current.make || '').toLowerCase() === brand;
    if (mine) {
      const label = tailored.querySelector('[data-brand-active-label]');
      if (label) label.textContent = current.nickname || current.label || current.key;
    }
    tailored.hidden = !mine;
    if (finder) finder.hidden = mine;
  }

  /* ---- Machine families --------------------------------------------------- */

  // The family is the model's first word, or two when the first is a prefix
  // shared across unrelated machines ("Pro RMK" vs "Pro-Ride", "Trail Boss").
  const TWO_WORD = new Set(['pro', 'trail', 'big', 'sport', 'wide', 'xpress', 'pro-ride']);

  function familyOf(model) {
    const words = String(model).split(/\s+/);
    if (words.length > 1 && TWO_WORD.has(words[0].toLowerCase())) return `${words[0]} ${words[1]}`;
    return words[0];
  }

  function families(brand) {
    const data = window.FitmentData;
    const out = new Map();
    for (const type of data.options('type')) {
      const make = data.options('make', { type }).find((m) => m.toLowerCase() === brand);
      if (!make) continue;
      for (const year of data.options('year', { type, make })) {
        for (const model of data.options('model', { type, make, year })) {
          const family = familyOf(model);
          const key = `${type}|${family}`;
          const entry = out.get(key) || { type, make, family, count: 0 };
          entry.count++;
          out.set(key, entry);
        }
      }
    }
    return [...out.values()].sort((a, b) => b.count - a.count);
  }

  function familyTile(f) {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'brand__family';
    button.setAttribute('data-garage-modal-open', '');
    button.dataset.garageModalPreset = JSON.stringify({ type: f.type, make: f.make, modelPrefix: f.family });
    button.innerHTML = '<span class="brand__family-name display-sm"></span><span class="brand__family-meta"></span>';
    button.querySelector('.brand__family-name').textContent = f.family;
    button.querySelector('.brand__family-meta').textContent = `${f.type} · ${f.count} model${f.count === 1 ? '' : 's'}`;
    li.appendChild(button);
    return li;
  }

  function paintFamilies(root) {
    const section = root.querySelector('[data-brand-families]');
    const list = root.querySelector('[data-brand-families-list]');
    if (!section || !list || !window.FitmentData) return Promise.resolve();
    return window.FitmentData.load()
      .then(() => {
        const items = families((root.dataset.brand || '').toLowerCase()).slice(0, 12);
        if (!items.length) return;
        list.replaceChildren(...items.map(familyTile));
        section.hidden = false;
      })
      .catch(() => { /* no taxonomy, no tiles */ });
  }

  /* ---- Boot ------------------------------------------------------------------ */

  function boot() {
    const root = page();
    if (!root) return;
    prune(root); // tiles for modules that were never rendered (no sources)
    fillRows(root);
    paintActive(root);
    paintFamilies(root);
  }

  // A deferred script runs while the document is already 'interactive', so
  // the scripts after this one (the garage, the fitment data) haven't run
  // yet. DOMContentLoaded fires after every deferred script has.
  if (document.readyState === 'complete') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('garage:changed', () => {
    const root = page();
    if (root) paintActive(root);
  });

  window.BrandPage = { fillRows, familyOf, families, prune, rowUrl };
})();
